import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store';
import {
  createSegment,
  updateSegment,
  fetchSegment,
  testSegment,
  selectCurrentSegment,
  selectPushLoading,
  selectPushError,
  setCurrentSegment,
} from 'store/slices/push.slice';
import { selectApplications } from 'store/slices/applications.slice';
import { SegmentCondition, SegmentCreateRequest } from 'types/push.types';
import {
  Form,
  Input,
  Button,
  Card,
  Row,
  Col,
  Select,
  Space,
  Typography,
  Alert,
  Divider,
  Spin,
  Statistic,
  message,
} from 'antd';
import {
  ArrowLeftOutlined,
  SaveOutlined,
  ExperimentOutlined,
} from '@ant-design/icons';
import { getStoredAppId } from 'utils/pushCenterStorage';
import SegmentConditionsBuilder from './SegmentConditionsBuilder';

const { Title } = Typography;
const { TextArea } = Input;

const SegmentForm: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const [form] = Form.useForm();

  const currentSegment = useAppSelector(selectCurrentSegment);
  const applications = useAppSelector(selectApplications);
  const isLoading = useAppSelector(selectPushLoading);
  const error = useAppSelector(selectPushError);

  const isEditMode = Boolean(id);
  const stateAppId = location.state?.appId ?? getStoredAppId();

  const [conditions, setConditions] = useState<SegmentCondition[]>([]);
  const [testResult, setTestResult] = useState<number | null>(null);
  const [isTesting, setIsTesting] = useState(false);

  useEffect(() => {
    if (isEditMode && id) {
      dispatch(fetchSegment(Number(id)));
    }

    return () => {
      dispatch(setCurrentSegment(null));
    };
  }, [dispatch, id, isEditMode]);

  useEffect(() => {
    if (currentSegment && isEditMode) {
      form.setFieldsValue({
        app_id: currentSegment.app_id,
        name: currentSegment.name,
        description: currentSegment.description,
      });
      setConditions(currentSegment.conditions || []);
    } else if (stateAppId) {
      form.setFieldValue('app_id', stateAppId);
    }
  }, [currentSegment, isEditMode, stateAppId, form]);

  const handleTest = async () => {
    try {
      const values = await form.validateFields(['app_id']);

      if (conditions.length === 0) {
        message.warning('Добавьте хотя бы одно условие');
        return;
      }

      setIsTesting(true);
      const result = await dispatch(
        testSegment({
          app_id: values.app_id,
          name: form.getFieldValue('name') || 'Test',
          description: form.getFieldValue('description') || '',
          conditions,
        }),
      ).unwrap();

      setTestResult(result.user_count);
      message.success('Тест выполнен успешно');
    } catch (err: any) {
      message.error(err.message || 'Ошибка тестирования');
    } finally {
      setIsTesting(false);
    }
  };

  const handleSubmit = async (values: any) => {
    if (conditions.length === 0) {
      message.error('Добавьте хотя бы одно условие');
      return;
    }

    const data: SegmentCreateRequest = {
      app_id: values.app_id,
      name: values.name,
      description: values.description || '',
      conditions,
    };

    try {
      if (isEditMode && id) {
        await dispatch(updateSegment({ id: Number(id), data })).unwrap();
        message.success('Сегмент обновлен');
      } else {
        await dispatch(createSegment(data)).unwrap();
        message.success('Сегмент создан');
      }
      navigate('/segments');
    } catch (err: any) {
      message.error(err.message || 'Ошибка сохранения');
    }
  };

  if (isLoading && isEditMode && !currentSegment) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" tip="Загрузка сегмента..." />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Row align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/segments')}
            style={{ marginRight: 16 }}
          >
            Назад
          </Button>
        </Col>
        <Col>
          <Title level={2} style={{ margin: 0 }}>
            {isEditMode ? 'Редактирование сегмента' : 'Создание сегмента'}
          </Title>
        </Col>
      </Row>

      {error && (
        <Alert
          message="Ошибка"
          description={error}
          type="error"
          closable
          style={{ marginBottom: 24 }}
        />
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          app_id: stateAppId,
        }}
      >
        <Row gutter={24}>
          <Col xs={24} lg={16}>
            <Card title="Основная информация" style={{ marginBottom: 24 }}>
              <Form.Item
                label="Приложение"
                name="app_id"
                rules={[{ required: true, message: 'Выберите приложение' }]}
              >
                <Select
                  placeholder="Выберите приложение"
                  disabled={isEditMode}
                  size="large"
                >
                  {applications.map((app) => (
                    <Select.Option key={app.id} value={app.id}>
                      {app.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                label="Название сегмента"
                name="name"
                rules={[
                  { required: true, message: 'Введите название' },
                  { min: 3, message: 'Минимум 3 символа' },
                ]}
              >
                <Input
                  placeholder="Например: Активные пользователи - iOS"
                  size="large"
                />
              </Form.Item>

              <Form.Item label="Описание" name="description">
                <TextArea rows={3} placeholder="Опишите этот сегмент..." />
              </Form.Item>
            </Card>

            <SegmentConditionsBuilder
              conditions={conditions}
              onChange={setConditions}
            />
          </Col>

          <Col xs={24} lg={8}>
            <Card title="Тестирование сегмента" style={{ marginBottom: 24 }}>
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <Typography.Text type="secondary">
                  Проверьте, сколько пользователей соответствует условиям сегмента
                </Typography.Text>

                <Button
                  block
                  icon={<ExperimentOutlined />}
                  onClick={handleTest}
                  loading={isTesting}
                  disabled={conditions.length === 0}
                >
                  {isTesting ? 'Тестирование...' : 'Тестировать сегмент'}
                </Button>

                {testResult !== null && (
                  <>
                    <Divider />
                    <Statistic
                      title="Найдено пользователей"
                      value={testResult}
                      valueStyle={{ color: '#1890ff', textAlign: 'center' }}
                    />
                  </>
                )}
              </Space>
            </Card>

            <Card title="Действия">
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <Button
                  block
                  type="primary"
                  size="large"
                  icon={<SaveOutlined />}
                  htmlType="submit"
                  loading={isLoading}
                >
                  {isEditMode ? 'Обновить сегмент' : 'Создать сегмент'}
                </Button>

                <Button block onClick={() => navigate('/segments')}>
                  Отмена
                </Button>
              </Space>
            </Card>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default SegmentForm;